import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SettingsStorageService} from './settings-storage-service.service';
import {MessageBusService} from './message-bus.service';
import {ShellClientService} from './shell-communication/shell-client.service';
import {GetOptionChainShellResponse, OptionExpirationDescriptor} from './shell-communication/shell-dto-protocol';
import {GetOptionChainShell} from './shell-communication/shell-operations-protocol';
import {PrefetchOptionChainUIMessage} from './ui-messages/ui-messages';
import {daysToExpiration, getStrikeStep, isValidNumber, isVoid} from './utils';
import {TimestampsService} from "./timestamps.service";
import {makeOptionTicker, OptionType, parseOptionTicker} from "./options-common/options.model";
import {DateTime} from "luxon";
import {SessionService} from "./authentication/session-service.service";
import {LastQuoteCacheService} from "./last-quote-cache.service";
import {QuoteDto} from "./shell-communication/dtos/quote-dto.class";
import {ShellTodayService} from './shell-clock/shell-today.service';


@Injectable({providedIn: 'root'})
export class OptionsChainService {

    constructor(
        private _layoutService: SettingsStorageService,
        private _shellClient: ShellClientService,
        private _messageBus: MessageBusService,
        private _timestampService: TimestampsService,
        private _sessionData: SessionService,
        private _lastQuoteCache: LastQuoteCacheService,
        private readonly _shellToday?: ShellTodayService,
    ) {

    }

    private _unsubscriber = new Subject<void>();
    private _chainsIndex: Record<string, GetOptionChainShellResponse> = {};
    private _promisesIndex: Record<string, Promise<GetOptionChainShellResponse>> = {};
    private _chainVersions: Record<string, number> = {};
    private _chainConfirmed: Record<string, boolean> = {};
    private _refreshContexts: Record<string, string> = {};
    private _streamModes: Record<string, string> = {};
    private _nextChainVersion = 0;

    //
    init(): Promise<void> {

        const start = Date.now();

        if (this._unsubscriber) {
            this._unsubscriber.next();
            this._unsubscriber.complete();
        }

        this._unsubscriber = new Subject<void>();

        this._chainsIndex = {};

        this._promisesIndex = {};
        this._chainVersions = {}; this._chainConfirmed = {}; this._refreshContexts = {}; this._streamModes = {};

        // The service outlives a logout/login (hash routing never reloads the SPA). A replay cursor
        // learned in a previous QG session must not label the next session's chains: seen on
        // staging 2026-09-18 as "(466d)" on today's expiration in a live shell.
        this._cursorDate = null;

        this._messageBus.of<PrefetchOptionChainUIMessage>('PrefetchOptionChainUIMessage')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(x => this.prefetchOptionChain(x.payload).catch(() => {}));

        this._messageBus.of<any>('Qg.JumpToDateChangedMessage')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(x => this.onQgJumpToDateChanged(x.payload));

        this._messageBus.of<any>('Qg.CursorDateChangedMessage')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(x => this.onQgCursorDateChanged(x.payload));

        // CR-20260918-003: the shell answers the chain AS OF the replay cursor, so the strike list
        // changes when a stream enters / leaves History mode. The message is published AFTER the
        // shell acknowledged the mode command, so a refetch here sees the new mode.
        this._messageBus.of<any>('Qg.StreamModeChanged')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(x => {
                this._streamModes[x.payload?.stream] = x.payload?.mode;
                if (x.payload?.mode !== 'History') { this._cursorDate = null; }
                this.refetchChainForCursor(x.payload?.stream, null);
            });

        const favs: string[] = this._layoutService.getItem<string[]>('favorites') || [];

        // always prefetch the three core chains: SPX, XSP, SPY
        ['SPX', 'XSP', 'SPY'].forEach(core => {
            if (favs.indexOf(core) < 0) {
                favs.push(core);
            }
        });

        favs.forEach(ticker => this.prefetchOptionChain({ticker}).catch(() => {}));

        const end = Date.now();

        this._messageBus.publish({
            topic: 'ServiceInitialized',
            payload: {
                time: end - start,
                name: 'OptionChains'
            }
        });

        return Promise.resolve(null);
    }

    //
    async getChain(ticker: string): Promise<GetOptionChainShellResponse> {

        if (isVoid(ticker)) {
            return null;
        }

        const pending = this._promisesIndex[ticker];
        if (pending) { return pending; }
        const existing = this.isChainConfirmed(ticker) ? this._chainsIndex[ticker] : null;

        if (existing && existing.expirations.length > 0) {

            return existing;
        }

        delete this._chainsIndex[ticker];

        const promise = this._promisesIndex[ticker];

        if (promise) {
            return promise;
        }

        const result = await this.prefetchOptionChain({ticker});

        return result;
    }

    getCachedChain(ticker: string): GetOptionChainShellResponse | null {
        if (isVoid(ticker)) {
            return null;
        }
        return this._chainsIndex[ticker] ?? null;
    }

    isChainConfirmed(ticker: string): boolean {
        return !!this._chainsIndex[ticker] && this._chainConfirmed[ticker] !== false;
    }

    private _cursorDate: string;

    /**
     * The replay cursor is a Quote Generator concept. Only a QG shell may count days from it;
     * a live shell counts from the clock, whatever the field holds (same rule as ShellTodayService).
     */
    private get effectiveCursorDate(): string | null {
        const login = this._sessionData.loginResult;
        const isQgShell = !!login && !!login.isQuoteGeneratorShell;
        return isQgShell && !isVoid(this._cursorDate) ? this._cursorDate : null;
    }

    isOptionExpired(ticker: string): boolean {

        const optionTicker = parseOptionTicker(ticker);

        if (isVoid(optionTicker)) {
            return true;
        }

        const isExpired = this.isExpirationExpired(optionTicker.expiration);

        return isExpired;
    }

    /** Count from the same live/history date used by option-chain labels. */
    getDaysToExpiration(expiration: string): number {
        return daysToExpiration(expiration, this.effectiveCursorDate);
    }

    isExpirationExpired(expiration: string): boolean {

        let timezoneNow: DateTime;

        const cursor = this.effectiveCursorDate;

        if (!isVoid(cursor)) {
            timezoneNow = DateTime.fromISO(cursor, {zone: 'America/New_York'});
        } else {
            timezoneNow = DateTime.local().setZone('America/New_York');
        }

        const expirationDate = DateTime.fromISO(expiration + 'T16:20:00', {zone: 'America/New_York'});

        const isExpired = timezoneNow > expirationDate;

        return isExpired;
    }

    //
    /**
     * Returns the subset of entries whose strike is NOT in the cached chain for `ticker`
     * (or whose expiration is missing). Awaits chain load if not yet cached.
     *
     * If the chain cannot be loaded at all (network/auth failure), returns [] —
     * "no claim of missing": caller should proceed normally; downstream code handles
     * its own validation. Refusing to claim avoids false rejection of valid pastes
     * during transient chain-load races.
     */
    async getMissingStrikes(
        ticker: string,
        entries: { expiration: string; strike: number }[]
    ): Promise<{ expiration: string; strike: number }[]> {

        if (isVoid(ticker) || isVoid(entries) || entries.length === 0) {
            return [];
        }

        const chain = await this.getChain(ticker);

        if (isVoid(chain) || isVoid(chain.expirations)) {
            console.warn(`getMissingStrikes: chain unavailable for ${ticker} — skipping pre-check`);
            return [];
        }

        const missing: { expiration: string; strike: number }[] = [];

        entries.forEach(entry => {
            const exp = chain.expirations.find(e => e.optionExpirationDate === entry.expiration);
            if (isVoid(exp) || exp.strikes.indexOf(entry.strike) < 0) {
                missing.push(entry);
            }
        });

        return missing;
    }

    //
    /**
     * For each (expiration, strike) pair NOT already in the cached chain for `ticker`,
     * probe Call AND Put quotes in parallel (~3s ceiling each via maxAttempts=12).
     * If either side returns a valid quote, insert the strike into the chain in memory
     * and republish 'Qg.ChainsUpdatedMessage' so existing consumers re-bind.
     *
     * Returns the entries that could NOT be reconciled (no quote on either side, or
     * the matching expiration is missing from the cache). Caller decides how to surface.
     *
     * Inserted strikes are session-memory only; the next prefetchOptionChain wipes them.
     */
    async tryReconcileMissingStrikes(
        ticker: string,
        entries: { expiration: string; strike: number }[]
    ): Promise<{ unresolved: { expiration: string; strike: number }[] }> {

        if (isVoid(ticker) || isVoid(entries) || entries.length === 0) {
            return { unresolved: [] };
        }

        const chain = this._chainsIndex[ticker];

        if (isVoid(chain)) {
            return { unresolved: entries.slice() };
        }

        const unresolved: { expiration: string; strike: number }[] = [];

        const missing: { exp: OptionExpirationDescriptor; entry: { expiration: string; strike: number } }[] = [];

        entries.forEach(entry => {

            const exp = chain.expirations.find(e => e.optionExpirationDate === entry.expiration);

            if (isVoid(exp)) {
                unresolved.push(entry);
                return;
            }

            if (exp.strikes.indexOf(entry.strike) >= 0) {
                return;
            }

            missing.push({ exp, entry });
        });

        if (missing.length === 0) {
            return { unresolved };
        }

        const probes = missing.map(async m => {

            const callTicker = makeOptionTicker(m.exp, OptionType.Call, m.entry.strike);
            const putTicker = makeOptionTicker(m.exp, OptionType.Put, m.entry.strike);

            const [callQ, putQ] = await Promise.all([
                isVoid(callTicker)
                    ? Promise.resolve<QuoteDto>(null)
                    : this._lastQuoteCache.getLastQuoteWithAwait(callTicker, 12),
                isVoid(putTicker)
                    ? Promise.resolve<QuoteDto>(null)
                    : this._lastQuoteCache.getLastQuoteWithAwait(putTicker, 12),
            ]);

            const ok = isQuoteTradable(callQ) || isQuoteTradable(putQ);

            return { m, ok };
        });

        const results = await Promise.all(probes);

        let inserted = false;

        results.forEach(r => {
            if (!r.ok) {
                unresolved.push(r.m.entry);
                return;
            }
            if (r.m.exp.strikes.indexOf(r.m.entry.strike) < 0) {
                r.m.exp.strikes.push(r.m.entry.strike);
                r.m.exp.strikes.sort((a, b) => a - b);
                inserted = true;
            }
        });

        if (inserted) {
            this._messageBus.publish({
                topic: 'Qg.ChainsUpdatedMessage',
                payload: {}
            });
        }

        return { unresolved };
    }


    //
    private prefetchOptionChain(msg: PrefetchOptionChainUIMessage): Promise<GetOptionChainShellResponse> {
        const ticker = msg.ticker, version = ++this._nextChainVersion;
        this._chainVersions[ticker] = version; this._chainConfirmed[ticker] = false;
        const request = this._shellClient.processQuery<GetOptionChainShellResponse>(new GetOptionChainShell(ticker));
        const result = request.then(chain => {
            if (this._chainVersions[ticker] !== version) {
                // A later request owns this context. Its caller must not receive the obsolete chain.
                return this._promisesIndex[ticker] || this._chainsIndex[ticker] || null;
            }
            chain.expirations = removeDuplicatesByExpirationDate(chain.expirations);
            chain.expirations.forEach(exp => {
                this.updateDaysToExpiration(exp, this._cursorDate);
                exp.strikeStep = getStrikeStep(exp.underlyingSymbol);
            });
            this._chainsIndex[ticker] = chain; this._chainConfirmed[ticker] = true;
            return chain;
        }).finally(() => {
            if (this._chainVersions[ticker] === version) { delete this._promisesIndex[ticker]; }
        });
        this._promisesIndex[ticker] = result;
        return result;
    }
    private updateDaysToExpiration(exp: OptionExpirationDescriptor, startDate?: string) {

        this._cursorDate = startDate;



        // Store what we were told, count from it only in a QG shell.
        const fromDate = this._shellToday?.todayKey(exp.underlyingTicker)
            || (this._sessionData.loginResult?.isQuoteGeneratorShell ? startDate || this._cursorDate : undefined);
        const diffIndays = daysToExpiration(exp.optionExpirationDate, fromDate);

        const displayDate = `${exp.optionExpirationDisplayDate} (${diffIndays}d)`;

        exp.daysToExpiration = diffIndays;

        exp.dateWithDaysToExpiration = displayDate;

        exp.visible = exp.daysToExpiration >= 0;
    }

    /**
     * CR-20260918-003. The shell answers the chain AS OF the replay cursor (strikes listed by that
     * day), so a mode change, a jump to another date or a cursor day change means a NEW strike list,
     * not just new day counts. Refetch; the cached chain keeps serving until the answer lands, then
     * 'Qg.ChainsUpdatedMessage' makes every picker re-bind. If the fetch fails, relabel what we have
     * with `fallbackDate` (null = the known cursor). Nothing to do when no chain was ever fetched
     * for the ticker.
     */
    private refetchChainForCursor(ticker: string, fallbackDate: string | null) {
        if (isVoid(ticker)) {
            return;
        }

        const chain = this._chainsIndex[ticker];
        if ((!chain || !chain.expirations) && !this._promisesIndex[ticker]) {
            return;
        }

        const context = (this._streamModes[ticker] || '') + '|' + (fallbackDate || this._cursorDate || '').split('T')[0];
        if (this._refreshContexts[ticker] === context && this._promisesIndex[ticker]) { return; }
        this._refreshContexts[ticker] = context;
        const request = this.prefetchOptionChain({ticker}), version = this._chainVersions[ticker];
        this._messageBus.publish({topic: 'Qg.ChainsChangingMessage', payload: {ticker}});
        request.then(() => {
            if (this._chainVersions[ticker] === version) {
                this._messageBus.publish({topic: 'Qg.ChainsUpdatedMessage', payload: {ticker}});
            }
        }).catch(() => {
            if (this._chainVersions[ticker] === version) {
                // Old cached series remain display data, never proof for a new Preview.
                this._messageBus.publish({topic: 'Qg.ChainsChangingMessage', payload: {ticker, failed: true}});
            }
        });
    }
    private onQgJumpToDateChanged(payload: { ticker: string, jumpToDate: string }) {
        const chain = this._chainsIndex[payload.ticker];
        if (!chain) {
            return;
        }

        let startDate = payload.jumpToDate;

        if (isVoid(startDate)) {
            startDate = this._timestampService
                .getDateTimeInZoneFormat(new Date(), 'America/New_York', 'yyyy-MM-dd');
        }

        // Record the day now (the first cursor tick after the jump is judged against it), then
        // refetch the strike list for that day (CR-20260918-003). The old relabel-only path kept
        // the all-time list fetched before the stream entered History mode.
        this._cursorDate = startDate;

        this.refetchChainForCursor(payload.ticker, startDate);
    }

    private onQgCursorDateChanged(payload: { ticker: string, cursorDate: string }) {
        if (payload.cursorDate && this._streamModes[payload.ticker] && this._streamModes[payload.ticker] !== 'History') { return; }
        if (isVoid(payload.cursorDate)) {
            this._cursorDate = null;
            return;
        }

        // CR-20260916-005: the FIRST cursor relabels too. Before, a chain fetched while the cursor
        // was still unknown (QG panel closed) kept browser-clock DTEs until the next DAY flip.
        const first = isVoid(this._cursorDate);
        const currentDate = first ? null : this._cursorDate.split('T')[0];
        const newDate = payload.cursorDate.split('T')[0];

        if (!first && currentDate === newDate) {
            return;
        }

        this._cursorDate = payload.cursorDate;

        // The cursor may arrive for a stream whose chain was never fetched here (the
        // ShellTodayService broadcasts it with the QG panel closed) — nothing to refresh then.
        const chain = this._chainsIndex[payload.ticker];
        if (!chain || !chain.expirations) {
            return;
        }

        // CR-20260918-003: a new cursor DAY means a new strike list (see refetchChainForCursor).
        this.refetchChainForCursor(payload.ticker, payload.cursorDate);
    }
}

function isQuoteTradable(q: QuoteDto): boolean {
    if (isVoid(q)) {
        return false;
    }
    return isValidNumber(q.mid, true) || isValidNumber(q.lastPx, true);
}

function removeDuplicatesByExpirationDate<T extends { optionExpirationDate: string }>(
    array: T[]
): T[] {
    const seen = new Set<string>();
    let writeIndex = 0;

    for (let readIndex = 0; readIndex < array.length; readIndex++) {
        const expirationDate = array[readIndex].optionExpirationDate;

        if (!seen.has(expirationDate)) {
            seen.add(expirationDate);
            array[writeIndex] = array[readIndex];
            writeIndex++;
        }
    }

    // Remove extra elements from the end
    array.length = writeIndex;

    return array;
}
